import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Loader2 } from 'lucide-react';
import api from '@/api';
import { cancelAccountDeletion } from '@/api/account';
import { useAuth } from '@/auth/AuthContext';
import { useEntitlements } from '@/hooks/useEntitlements';
import { notifications } from '@/utils/notifications';
import { RequestAccountDeletionModal } from '@/components/modals/RequestAccountDeletionModal';

type ActiveWorkspace = {
  id: string;
  name: string;
  adminId: string;
  users: { uid: string; status: boolean }[];
};

export function AccountDangerZone() {
  const { t } = useTranslation('settings');
  const { t: tToast } = useTranslation('notifications');
  const { user } = useAuth();
  const { pendingDeletion, refresh, loading: entitlementsLoading } = useEntitlements();
  const [workspace, setWorkspace] = useState<ActiveWorkspace | null>(null);
  const [loadingWorkspace, setLoadingWorkspace] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const loadWorkspace = useCallback(async () => {
    try {
      setLoadingWorkspace(true);
      const response = await api.get<{
        status: string;
        accounts: ActiveWorkspace[];
        aid: string;
      }>('/users/accounts');
      if (response.data.status === 'success') {
        const active = response.data.accounts.find((a) => a.id === response.data.aid) ?? null;
        setWorkspace(active);
      }
    } catch (err) {
      console.error('[AccountDangerZone] load failed', err);
    } finally {
      setLoadingWorkspace(false);
    }
  }, []);

  useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  const isAdmin = workspace?.adminId === user?.id;
  const memberCount = workspace?.users?.length ?? 1;

  const handleCancelDeletion = async () => {
    try {
      setCanceling(true);
      await cancelAccountDeletion();
      await refresh();
      notifications.success(tToast('accountDeletion.canceled'));
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : t('account.dangerZone.cancelFailed'));
    } finally {
      setCanceling(false);
    }
  };

  if (loadingWorkspace || entitlementsLoading) {
    return (
      <section className="space-y-4">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">{t('account.dangerZone.loading')}</span>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <header className="flex items-baseline gap-3">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-red-500/10 text-red-500">
          <Trash2 size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-bold text-foreground">{t('account.dangerZone.title')}</h3>
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
            {t('account.dangerZone.description')}
          </p>
        </div>
      </header>

      <div className="rounded-2xl border border-red-500/20 bg-red-500/[0.03] p-5 sm:p-6">
        {pendingDeletion ? (
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              {t('account.deletionPending.message', {
                date: new Date(pendingDeletion.scheduledAt).toLocaleDateString(undefined, {
                  dateStyle: 'long',
                }),
              })}
            </p>
            <p className="text-xs text-muted-foreground">{t('account.deletionPending.readOnlyHint')}</p>
            {isAdmin ? (
              <button
                type="button"
                disabled={canceling}
                onClick={() => void handleCancelDeletion()}
                className="rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-bold text-foreground transition-all hover:bg-muted disabled:opacity-50"
              >
                {canceling ? t('account.deletionPending.canceling') : t('account.deletionPending.cancel')}
              </button>
            ) : null}
          </div>
        ) : isAdmin ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('account.dangerZone.adminHint')}</p>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="rounded-xl bg-red-500 px-4 py-2.5 text-sm font-bold text-white transition-all hover:bg-red-600"
            >
              {t('account.dangerZone.deleteButton')}
            </button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('account.dangerZone.nonAdminHint')}</p>
        )}
      </div>

      <RequestAccountDeletionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        workspaceName={workspace?.name ?? t('account.dangerZone.unnamedWorkspace')}
        memberCount={memberCount}
        onSuccess={() => void refresh()}
      />
    </section>
  );
}
